import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Plus, Loader2, FileDown, ShieldAlert, Eye, CheckCircle2, CircleDashed, Building2 } from "lucide-react";

const SOURCES = [
  "salarie_id", "salarie_nom", "periode", "heures_validees", "heures_signees",
  "frais_valides_eur", "absences_jours", "evenements", "anomalies",
  "contrat.type_contrat", "contrat.date_entree", "contrat.date_sortie", "contrat.salaire_brut",
];
const EMPTY_COL = { name: "", source: "salarie_nom", obligatoire: false, mapping: "" };
const EMPTY = { nom: "", logiciel: "", logiciel_version: "", nature: "variables_paie", separateur: ";", encodage: "utf-8", avec_entete: true, format_date: "YYYY-MM-DD", format_montant: "0.00", colonnes: [{ ...EMPTY_COL }], partie_double: false };

export default function ExportProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [exportDlg, setExportDlg] = useState(null);   // profile
  const [lotSel, setLotSel] = useState("");
  const [preview, setPreview] = useState(null);
  const [feedback, setFeedback] = useState({ export_id: "", resultat: "accepte", anomalies: "", preuve: "" });
  const [fbFor, setFbFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([api.get("/export-profiles"), api.get("/payroll/lots")]);
      setProfiles(p.data); setLots(l.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const colonnes = form.colonnes.filter(c => c.name.trim());
      if (!colonnes.length) return toast.error("Au moins une colonne est requise");
      await api.post("/export-profiles", { ...form, colonnes });
      toast.success("Profil d'export créé — compatibilité cabinet NON validée par défaut");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runPreview = async () => {
    if (!lotSel) return toast.error("Sélectionnez un lot");
    try {
      const r = await api.post(`/payroll/lots/${lotSel}/export-v2`, null, { params: { profile_id: exportDlg.id, preview: true } });
      setPreview(r.data);
      if (r.data.erreurs_bloquantes?.length) toast.error(`${r.data.erreurs_bloquantes.length} erreur(s) bloquante(s)`);
      else toast.success("Prévisualisation sans erreur bloquante");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runExport = async () => {
    try {
      const r = await api.post(`/payroll/lots/${lotSel}/export-v2`, null, { params: { profile_id: exportDlg.id, preview: false } });
      toast.success(`Export produit — empreinte ${r.data.empreinte_sha256.slice(0, 12)}…`);
      setFeedback({ ...feedback, export_id: r.data.export_id });
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? `Export bloqué : ${det.erreurs?.length || 0} erreur(s)` : formatApiError(det));
    }
  };

  const download = async (exportId) => {
    const r = await api.get(`/exports/${exportId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `export-${exportId.slice(0, 8)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const sendFeedback = async () => {
    try {
      await api.post(`/export-profiles/${fbFor.id}/import-feedback`, feedback);
      toast.success("Résultat d'import cabinet enregistré");
      setFbFor(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="export-profiles-page">
      <PageHeader
        title="Profils d'export — paie & précomptabilité"
        subtitle="Exports configurables, jamais un FEC réglementaire complet. Validation cabinet : étape extérieure à confirmer avec preuve."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="profile-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white"><Plus className="h-4 w-4 mr-2" /> Nouveau profil</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="prof-new-desc">
              <DialogHeader>
                <DialogTitle>Profil d'export</DialogTitle>
                <DialogDescription id="prof-new-desc">Ne choisit pas arbitrairement un logiciel : renseignez le logiciel réellement utilisé par le cabinet, sinon la compatibilité restera « non validée ».</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nom *</Label><Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} data-testid="prof-nom" /></div>
                <div className="space-y-2"><Label>Logiciel cible *</Label><Input value={form.logiciel} onChange={e => setForm({ ...form, logiciel: e.target.value })} placeholder="nom du logiciel du cabinet" data-testid="prof-logiciel" /></div>
                <div className="space-y-2"><Label>Version logiciel</Label><Input value={form.logiciel_version} onChange={e => setForm({ ...form, logiciel_version: e.target.value })} /></div>
                <div className="space-y-2">
                  <Label>Nature de l'export *</Label>
                  <Select value={form.nature} onValueChange={v => setForm({ ...form, nature: v })}>
                    <SelectTrigger data-testid="prof-nature"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="variables_paie">Variables de paie</SelectItem>
                      <SelectItem value="ecritures_precompta">Écritures de précomptabilité</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Séparateur</Label>
                  <Select value={form.separateur} onValueChange={v => setForm({ ...form, separateur: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value=";">;</SelectItem><SelectItem value=",">,</SelectItem><SelectItem value="\t">Tabulation</SelectItem><SelectItem value="|">|</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Encodage</Label>
                  <Select value={form.encodage} onValueChange={v => setForm({ ...form, encodage: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="utf-8">UTF-8</SelectItem><SelectItem value="windows-1252">Windows-1252</SelectItem></SelectContent>
                  </Select>
                </div>
                {form.nature === "ecritures_precompta" && (
                  <div className="col-span-2 flex items-center gap-2">
                    <input type="checkbox" id="pd" checked={form.partie_double} onChange={e => setForm({ ...form, partie_double: e.target.checked })} data-testid="prof-partie-double" />
                    <Label htmlFor="pd">Écritures en partie double (l'équilibre débit/crédit sera vérifié)</Label>
                  </div>
                )}
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Colonnes (ordre conservé)</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, colonnes: [...form.colonnes, { ...EMPTY_COL }] })} data-testid="prof-add-col">+ Colonne</Button>
                  </div>
                  {form.colonnes.map((c, i) => {
                    const upd = (field, val) => setForm({ ...form, colonnes: form.colonnes.map((x, j) => (j === i ? { ...x, [field]: val } : x)) });
                    return (
                    <div key={i} className="flex gap-2 items-center">
                      <Input className="w-40" placeholder="Nom colonne *" value={c.name} onChange={(e) => upd("name", e.target.value)} data-testid={`prof-col-name-${i}`} />
                      <Select value={c.source} onValueChange={(v) => upd("source", v)}>
                        <SelectTrigger className="flex-1" data-testid={`prof-col-source-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="w-40" placeholder="Correspondance cabinet" value={c.mapping} onChange={(e) => upd("mapping", e.target.value)} />
                      <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                        <input type="checkbox" checked={c.obligatoire} onChange={(e) => upd("obligatoire", e.target.checked)} data-testid={`prof-col-oblig-${i}`} /> obligatoire
                      </label>
                    </div>
                    );
                  })}
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="prof-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Créer le profil</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Alert className="border-amber-300 bg-amber-50 mb-4" data-testid="export-not-fec-notice">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Profil logiciel à compléter — compatibilité cabinet non validée</AlertTitle>
        <AlertDescription className="text-xs">
          Tant qu'un essai réel n'a pas été accepté par le logiciel du cabinet (avec preuve enregistrée),
          aucun profil n'est présenté comme compatible. Ces exports ne sont pas un FEC réglementaire complet.
        </AlertDescription>
      </Alert>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> :
        profiles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="profiles-empty">
            <FileDown className="h-10 w-10 mx-auto text-slate-300" /><p className="mt-3 text-slate-500">Aucun profil d'export. Créez le profil correspondant au logiciel du cabinet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="profiles-list">
            {profiles.map(p => {
              const vc = p.validation_cabinet || {};
              return (
                <div key={p.id} className={`bg-white border rounded-xl p-4 ${p.actif ? "border-slate-200" : "border-slate-100 opacity-60"}`} data-testid={`profile-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <strong className="text-slate-900">{p.nom}</strong>
                        <Badge variant="outline">v{p.version}</Badge>
                        <Badge className={p.nature === "variables_paie" ? "bg-blue-100 text-blue-800" : "bg-violet-100 text-violet-800"}>
                          {p.nature === "variables_paie" ? "Variables de paie" : "Écritures précomptabilité"}
                        </Badge>
                        {!p.actif && <Badge variant="outline">remplacé</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {p.logiciel}{p.logiciel_version ? ` ${p.logiciel_version}` : ""} · séparateur « {p.separateur} » · {p.colonnes?.length || 0} colonnes · créé par {p.created_by}
                      </div>
                      <div className="flex gap-2 mt-2 text-[11px]">
                        <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> format configuré</span>
                        <span className={`flex items-center gap-1 ${vc.controles_internes_ok ? "" : "opacity-50"}`}>{vc.controles_internes_ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <CircleDashed className="h-3 w-3" />} contrôles internes {vc.controles_internes_ok ? "réussis" : "en attente"}</span>
                        <span className={`flex items-center gap-1 ${vc.import_cabinet_confirme?.confirme ? "" : "opacity-50"}`}>{vc.import_cabinet_confirme?.confirme ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <CircleDashed className="h-3 w-3" />} import cabinet {vc.import_cabinet_confirme?.confirme ? "confirmé" : "non confirmé"}</span>
                      </div>
                    </div>
                    {p.actif && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setExportDlg(p); setPreview(null); setLotSel(""); }} data-testid={`profile-export-${p.id}`}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Tester / Exporter
                        </Button>
                        {vc.controles_internes_ok && (
                          <Button size="sm" variant="outline" onClick={() => { setFbFor(p); setFeedback({ export_id: "", resultat: "accepte", anomalies: "", preuve: "" }); }} data-testid={`profile-feedback-${p.id}`}>
                            <Building2 className="h-3.5 w-3.5 mr-1" /> Résultat import
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={!!exportDlg} onOpenChange={v => { if (!v) { setExportDlg(null); setPreview(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="export-test-dialog" aria-describedby="export-test-desc">
          <DialogHeader>
            <DialogTitle>Exporter avec « {exportDlg?.nom} » v{exportDlg?.version}</DialogTitle>
            <DialogDescription id="export-test-desc">Prévisualisation avec rapport d'anomalies, puis export définitif si aucune erreur bloquante.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Lot de préparation *</Label>
              <Select value={lotSel} onValueChange={setLotSel}>
                <SelectTrigger data-testid="export-lot"><SelectValue placeholder="Sélectionner un lot" /></SelectTrigger>
                <SelectContent>{lots.map(l => <SelectItem key={l.id} value={l.id}>{l.periode} — v{l.version} — {l.status} — {l.dossiers?.length || 0} dossier(s)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={runPreview} data-testid="export-preview-btn">Prévisualiser + anomalies</Button>
              <Button onClick={runExport} disabled={!preview || preview.erreurs_bloquantes?.length > 0} data-testid="export-run-btn" className="bg-[#1B2A4A] text-white">
                <FileDown className="h-4 w-4 mr-1" /> Produire l'export définitif
              </Button>
            </div>
            {preview && (
              <div className="space-y-3" data-testid="export-preview">
                {preview.erreurs_bloquantes?.length > 0 && (
                  <Alert className="border-red-300 bg-red-50">
                    <ShieldAlert className="h-4 w-4 text-red-600" />
                    <AlertTitle>{preview.erreurs_bloquantes.length} erreur(s) bloquante(s) — export définitif impossible</AlertTitle>
                    <AlertDescription><ul className="text-xs list-disc pl-4">{preview.erreurs_bloquantes.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}</ul></AlertDescription>
                  </Alert>
                )}
                {preview.avertissements?.length > 0 && (
                  <Alert className="border-amber-300 bg-amber-50">
                    <AlertTitle>Avertissements (traçables)</AlertTitle>
                    <AlertDescription><ul className="text-xs list-disc pl-4">{preview.avertissements.map((a, i) => <li key={i}>{a}</li>)}</ul></AlertDescription>
                  </Alert>
                )}
                <div className="text-xs text-slate-500">
                  Empreinte prévue : <code>{preview.empreinte_sha256?.slice(0, 24)}…</code> · {preview.totaux_controle?.lignes} ligne(s) · {preview.totaux_controle?.heures_validees_total} h validées
                </div>
                <div className="border rounded overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {(preview.apercu || []).map((row, i) => (
                        <tr key={i} className={i === 0 ? "bg-slate-100 font-semibold" : "border-t border-slate-100"}>
                          {row.map((c, j) => <td key={j} className="px-2 py-1 whitespace-nowrap">{c}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {feedback.export_id && (
              <div className="flex gap-2 items-center border-t pt-3">
                <Badge className="bg-emerald-100 text-emerald-800">Export {feedback.export_id.slice(0, 8)} produit</Badge>
                <Button size="sm" variant="outline" onClick={() => download(feedback.export_id)} data-testid="export-download-btn">Retélécharger le même artefact</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fbFor} onOpenChange={v => { if (!v) setFbFor(null); }}>
        <DialogContent data-testid="feedback-dialog" aria-describedby="feedback-desc">
          <DialogHeader>
            <DialogTitle>Résultat d'import chez le cabinet</DialogTitle>
            <DialogDescription id="feedback-desc">La confirmation de compatibilité exige un essai effectivement accepté par le logiciel, avec sa preuve.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Identifiant d'export *</Label><Input value={feedback.export_id} onChange={e => setFeedback({ ...feedback, export_id: e.target.value })} placeholder="id de l'export produit" data-testid="fb-export-id" /></div>
            <div>
              <Label>Résultat *</Label>
              <Select value={feedback.resultat} onValueChange={v => setFeedback({ ...feedback, resultat: v })}>
                <SelectTrigger data-testid="fb-resultat"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="accepte">Import accepté</SelectItem><SelectItem value="refuse">Import refusé</SelectItem></SelectContent>
              </Select>
            </div>
            {feedback.resultat === "accepte" && (
              <div><Label>Preuve * (référence email, capture, compte-rendu)</Label><Input value={feedback.preuve} onChange={e => setFeedback({ ...feedback, preuve: e.target.value })} data-testid="fb-preuve" /></div>
            )}
            <div><Label>Anomalies constatées</Label><Input value={feedback.anomalies} onChange={e => setFeedback({ ...feedback, anomalies: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={sendFeedback} data-testid="fb-submit" className="bg-[#1B2A4A] text-white">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
